// FAZ-9 / MOD-18 — AD grup → CMS rol eşlemesi için kullanıcı-dostu editör.
//
// AntD `Form.Item name="groupRoleMapJson"` altında controlled bir alan olarak
// çalışır: `value` ham JSON string'i alır, `onChange` ham JSON string'i döndürür.
// İçeride satır bazlı (grup + roller) bir liste tutar; ham JSON görünümü toggle'ı
// ileri kullanıcı için doğrudan JSON düzenlemeye izin verir (geçersiz JSON'da
// hata gösterir, emit ETMEZ). Serileştirme mantığı `utils/groupRoleMap`'te
// birim-test edilmiştir.

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Input, Segmented, Select, Space, Typography } from 'antd';
import { Plus, Trash2 } from 'lucide-react';
import { LDAP_ASSIGNABLE_ROLES } from '../constants';
import {
  parseGroupRoleMap,
  serializeGroupRoleMap,
  type GroupRoleMapping,
} from '../utils/groupRoleMap';

const { Text } = Typography;

interface GroupRoleMapEditorProps {
  value?: string;
  onChange?: (value: string) => void;
}

function newRow(): GroupRoleMapping {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  const id = c?.randomUUID ? c.randomUUID() : `grm_${Math.random().toString(36).slice(2)}`;
  return { id, group: '', roles: [] };
}

const ROLE_OPTIONS = LDAP_ASSIGNABLE_ROLES.map((r) => ({ label: r, value: r }));

export function GroupRoleMapEditor({ value = '', onChange }: GroupRoleMapEditorProps) {
  const { t } = useTranslation(['ldap']);
  const [rows, setRows] = useState<GroupRoleMapping[]>(() => parseGroupRoleMap(value));
  const [view, setView] = useState<'rows' | 'raw'>('rows');
  const [rawText, setRawText] = useState(value);
  const [rawInvalid, setRawInvalid] = useState(false);
  // Kendi emit ettiğimiz değeri izleyerek DIŞ reset'i (form.setFieldsValue) ayırt et.
  const lastEmitted = useRef<string>(value);

  useEffect(() => {
    if (value !== lastEmitted.current) {
      lastEmitted.current = value;
      setRows(parseGroupRoleMap(value));
      setRawText(value);
      setRawInvalid(false);
    }
  }, [value]);

  const emit = (nextRows: GroupRoleMapping[]) => {
    setRows(nextRows);
    const json = serializeGroupRoleMap(nextRows);
    lastEmitted.current = json;
    onChange?.(json);
  };

  const updateRow = (id: string, patch: Partial<GroupRoleMapping>) =>
    emit(rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const removeRow = (id: string) => emit(rows.filter((r) => r.id !== id));

  const addRow = () => emit([...rows, newRow()]);

  const switchView = (next: 'rows' | 'raw') => {
    if (next === 'raw') {
      setRawText(serializeGroupRoleMap(rows));
      setRawInvalid(false);
    }
    setView(next);
  };

  const onRawChange = (text: string) => {
    setRawText(text);
    const trimmed = text.trim();
    if (!trimmed) {
      setRawInvalid(false);
      emit([]);
      return;
    }
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
        setRawInvalid(false);
        const nextRows = parseGroupRoleMap(text);
        setRows(nextRows);
        lastEmitted.current = text;
        onChange?.(text);
        return;
      }
    } catch {
      // düşer: aşağıda invalid işaretlenir
    }
    setRawInvalid(true);
  };

  return (
    <div data-testid="group-role-map-editor">
      <Space style={{ marginBottom: 12 }} align="center">
        <Segmented<'rows' | 'raw'>
          size="small"
          value={view}
          onChange={switchView}
          options={[
            { label: t('groupRoleMap.title'), value: 'rows' },
            { label: t('groupRoleMap.rawToggle'), value: 'raw' },
          ]}
        />
      </Space>

      {view === 'raw' ? (
        <>
          <Input.TextArea
            value={rawText}
            onChange={(e) => onRawChange(e.target.value)}
            autoSize={{ minRows: 3, maxRows: 10 }}
            spellCheck={false}
            aria-label={t('groupRoleMap.rawLabel')}
            status={rawInvalid ? 'error' : undefined}
          />
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
            {t('groupRoleMap.rawHelp')}
          </Text>
          {rawInvalid ? (
            <Alert
              type="error"
              showIcon
              message={t('groupRoleMap.rawInvalid')}
              style={{ marginTop: 8 }}
            />
          ) : null}
        </>
      ) : (
        <>
          {rows.length === 0 ? (
            <Text type="secondary">{t('groupRoleMap.empty')}</Text>
          ) : (
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              {rows.map((row) => (
                <Space key={row.id} align="start" style={{ width: '100%' }} wrap>
                  <Input
                    value={row.group}
                    onChange={(e) => updateRow(row.id, { group: e.target.value })}
                    placeholder={t('groupRoleMap.groupPlaceholder')}
                    aria-label={t('groupRoleMap.groupLabel')}
                    style={{ width: 240 }}
                  />
                  <Select
                    mode="multiple"
                    value={row.roles}
                    onChange={(roles: string[]) => updateRow(row.id, { roles })}
                    options={ROLE_OPTIONS}
                    placeholder={t('groupRoleMap.rolesPlaceholder')}
                    aria-label={t('groupRoleMap.rolesLabel')}
                    style={{ minWidth: 260 }}
                  />
                  <Button
                    type="text"
                    danger
                    icon={<Trash2 size={15} />}
                    aria-label={t('groupRoleMap.remove')}
                    onClick={() => removeRow(row.id)}
                  />
                </Space>
              ))}
            </Space>
          )}
          <Button
            type="dashed"
            icon={<Plus size={15} />}
            onClick={addRow}
            style={{ marginTop: 12 }}
          >
            {t('groupRoleMap.add')}
          </Button>
        </>
      )}
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
        {t('groupRoleMap.help')}
      </Text>
    </div>
  );
}
